import { useState, useRef, useEffect } from 'react';
import { Search, Mic, Clock, Star, MapPin, X, Filter } from 'lucide-react';
import { mapStyles } from '../../styles/mapTheme';

interface SearchResult {
  id: string;
  name: string;
  address: string;
  type: 'location' | 'recent' | 'saved' | 'category';
  coordinates?: [number, number];
}

interface SearchBarProps {
  onLocationSelect: (location: SearchResult) => void;
  onVoiceSearch: () => void;
  placeholder?: string;
}

const categories = [
  { id: 'extension', name: 'Extension Offices', icon: '🏢' },
  { id: 'veterinary', name: 'Veterinary Clinics', icon: '🏥' },
  { id: 'seeds', name: 'Seed Suppliers', icon: '🌱' },
  { id: 'equipment', name: 'Farm Equipment', icon: '🚜' },
  { id: 'markets', name: 'Markets', icon: '🏪' }
];

const lesothoLocations = [
  { id: '1', name: 'Maseru', address: 'Capital District, Lesotho', type: 'location' as const, coordinates: [-29.3151, 27.4869] as [number, number] },
  { id: '2', name: 'Berea', address: 'Berea District, Lesotho', type: 'location' as const, coordinates: [-29.0833, 27.7167] as [number, number] },
  { id: '3', name: 'Leribe', address: 'Leribe District, Lesotho', type: 'location' as const, coordinates: [-28.8719, 28.0378] as [number, number] },
  { id: '4', name: 'The Green Acorn Guest House', address: 'Maseru, Lesotho', type: 'location' as const, coordinates: [-29.3151, 27.4869] as [number, number] }
];

export default function SearchBar({ onLocationSelect, onVoiceSearch, placeholder = "Search locations..." }: SearchBarProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [recentSearches] = useState<SearchResult[]>([
    { id: 'r1', name: 'Maseru Agricultural Office', address: 'Maseru, Lesotho', type: 'recent' },
    { id: 'r2', name: 'Berea Seed Store', address: 'Berea, Lesotho', type: 'recent' }
  ]);
  const [savedPlaces] = useState<SearchResult[]>([
    { id: 's1', name: 'My Farm', address: 'Ha Abia, Maseru', type: 'saved' },
    { id: 's2', name: 'Extension Office', address: 'Maseru District', type: 'saved' }
  ]);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (value: string) => {
    setQuery(value);
    if (value.length > 0) {
      const filtered = lesothoLocations.filter(location =>
        location.name.toLowerCase().includes(value.toLowerCase()) ||
        location.address.toLowerCase().includes(value.toLowerCase())
      );
      setResults(filtered);
    } else {
      setResults([]);
    }
  };

  const handleSelect = (item: SearchResult) => {
    setQuery(item.name);
    setIsOpen(false);
    onLocationSelect(item);
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <div className={`${mapStyles.card} flex items-center px-4 py-3`}>
        <Search className="w-5 h-5 text-gray-400 mr-3" />
        <input
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => setIsOpen(true)}
          className="flex-1 outline-none text-gray-700 placeholder-gray-400"
        />
        {query && (
          <button onClick={clearSearch} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        )}
        <button onClick={onVoiceSearch} className="p-1 hover:bg-gray-100 rounded ml-2">
          <Mic className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {isOpen && (
        <div className={`absolute top-full left-0 right-0 mt-2 ${mapStyles.card} max-h-80 overflow-y-auto z-50`}>
          {results.length > 0 && (
            <div className="p-2">
              <div className="text-xs font-medium text-gray-500 px-3 py-2">Search Results</div>
              {results.map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleSelect(result)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded flex items-center space-x-3"
                >
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <div>
                    <div className="text-gray-700 font-medium">{result.name}</div>
                    <div className="text-xs text-gray-500">{result.address}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {query === '' && (
            <>
              {categories.length > 0 && (
                <div className="p-2">
                  <div className="text-xs font-medium text-gray-500 px-3 py-2">Categories</div>
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => handleSelect({ id: category.id, name: category.name, address: 'Category', type: 'category' })}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded flex items-center space-x-3"
                    >
                      <span className="text-lg">{category.icon}</span>
                      <span className="text-gray-700">{category.name}</span>
                    </button>
                  ))}
                </div>
              )}

              {recentSearches.length > 0 && (
                <div className="p-2 border-t border-gray-100">
                  <div className="text-xs font-medium text-gray-500 px-3 py-2">Recent</div>
                  {recentSearches.map((search) => (
                    <button
                      key={search.id}
                      onClick={() => handleSelect(search)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded flex items-center space-x-3"
                    >
                      <Clock className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-gray-700">{search.name}</div>
                        <div className="text-xs text-gray-500">{search.address}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {savedPlaces.length > 0 && (
                <div className="p-2 border-t border-gray-100">
                  <div className="text-xs font-medium text-gray-500 px-3 py-2">Saved Places</div>
                  {savedPlaces.map((place) => (
                    <button
                      key={place.id}
                      onClick={() => handleSelect(place)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded flex items-center space-x-3"
                    >
                      <Star className="w-4 h-4 text-yellow-500" />
                      <div>
                        <div className="text-gray-700">{place.name}</div>
                        <div className="text-xs text-gray-500">{place.address}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}